import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { togglePinnedPage } from 'components/GlobalState/commands/togglePinnedPage';
import { type FC, type MouseEventHandler, useState } from 'react';
import { selectAllRoutesByPath } from '../GlobalState/selectors/selectAllRoutesByPath';
import { NavigationLink } from './NavigationLink';
import type { MenuTreeNode } from './menuTree';

export interface NavigationPinnedPagesProps {
  compact: boolean;
}

const navigationLinkFixedPath = [0];

/**
 * Show pinned menu items in navigation.
 *
 * This now renders all pined pages at once as the feature is available only in Technology menu
 * and there are no plans to extend it elsewhere.
 */
export const NavigationPinnedPages: FC<NavigationPinnedPagesProps> = (
  props,
) => {
  const ctxProps = useGlobalStateContextSelectors({
    pinnedPages: prop('user', 'pinnedPages'),
    routesByPath: selectAllRoutesByPath,
    dispatch: prop('dispatch'),
  });
  const [hover, setHover] = useState<number | null>(null);

  return ctxProps.pinnedPages.map((urlPath, index) => {
    const handleClick: MouseEventHandler<HTMLElement> = (e) => {
      ctxProps.dispatch(togglePinnedPage, {
        urlPath,
      });
      setHover(null);
      e.stopPropagation();
      e.preventDefault();
    };

    const label = (() => {
      // Fixed noUncheckedIndexedAccess NIM-19408
      const route = ctxProps.routesByPath[urlPath]!;
      if (route.tableEndpoint) {
        return route.tableEndpoint.title;
      }

      stumble('Pinned page with undefined title');
      return urlPath;
    })();

    const labelComponent = (
      <div className='navigation__label flex items-center justify-between flex-1 max-w-full'>
        <div className='navigation__label-text flex-1 overflow-hidden'>
          {label}
        </div>

        <Icon
          className='mr-xs ml-2xs'
          glyph={hover === index ? 'fa-xmark' : 'fa-thumbtack'}
          iconStyle='solid'
          size='xs'
          onClick={handleClick}
          onMouseEnter={() => setHover(index)}
          onMouseLeave={() => setHover(null)}
        />
      </div>
    );

    const menuNode: MenuTreeNode = { label, urlList: [urlPath] };

    return (
      <NavigationLink
        key={`${index}-${urlPath}`}
        index={0}
        item={menuNode}
        url={urlPath ?? '/'}
        path={navigationLinkFixedPath}
        compact={props.compact}
        label={labelComponent}
        className={
          index === ctxProps.pinnedPages.length - 1
            ? 'pb-2xs mb-2xs after:absolute after:-bottom-0 after:bg-gray-600 after:w-full after:h-1px relative'
            : ''
        }
      />
    );
  });
};
